import React, {
  useCallback, useEffect, useState, ReactElement, ForwardedRef,
} from 'react';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import TextItalic from '../text-italic/text-italic.component';
import SearchDropdown from '../search-dropdown/search-dropdown.component';
import MenuItem from '../menu-item/menu-item.component';
import TextBlock from '../text-block/text-block.component';
import RequiredMark from '../required-mark/required-mark.component';
import FormHelper from '../form-helper/form-helper.component';
import FormatService from '../../services/format-service';
import useStylesFieldSelect from './field-select.styles';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';

const FieldSelect: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const VARIANT_OUTLINED: SelectVariant = 'outlined';
  const AUTOCOMPLETE_OFF: string = 'off';
  const LABEL_VARIANT_OUTLINED: LabelVariant = 'outlined';
  const FORM_CONTROL_VARIANT_OUTLINED: FormControlVariant = 'outlined';
  const ID_PREFIX_SELECT_INPUT: string = 'select-input';

  const {
    isOpen,
    isLight,
    isPlain,
    isShort,
    isDisabled,
    isReadOnly,
    isMultiple,
    isOutlined,
    isRequired,
    isBordered,
    withNoId,
    withNoWrap,
    withSearch,
    withIcons,
    withUnderline,
    withNoBorders,
    withFormHelper,
    withRightOffset,
    withBlankElement,
    withBlankItemDefault,
    label,
    id,
    className,
    searchOptions,
    blankElementLabel,
    blankElementValue,
    width,
    value,
    valueRendered,
    newItemComponent,
    optionsTotal,
    inputRefs,
    inputRefIndex,
    onOpen,
    onSearch,
    onClose,
    onChange,
    onBlankElementPointerDown,
    onBlankElementContextMenu,
    input,
    meta,
    children,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldSelect();

  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;
  const inputId: string | undefined = (!withNoId && !isPlain && input && input.name
    && `${ID_PREFIX_SELECT_INPUT}-${input.name}`) || undefined;
  const blankItemValue: string = blankElementValue !== undefined ? blankElementValue : '';
  const blankItemValueDefault: string[] | string = isMultiple ? [blankItemValue] : blankItemValue;
  const blankItemLabel: string = (blankElementLabel || blankElementLabel === '') ? blankElementLabel : 'None';
  const inputValue: string = value !== undefined ? value : ((input && input.value) || blankItemValueDefault);

  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldSelectNoUnderline : undefined;
  const iconClassName: string | undefined = withIcons ? classes.fieldSelectIcon : undefined;
  const lightClassName: string | undefined = isLight ? classes.fieldSelectLight : undefined;
  const borderedClassName: string | undefined = isBordered ? classes.fieldSelectBordered : undefined;
  const outlinedClassName: string | undefined = isOutlined ? classes.fieldSelectOutlined : undefined;
  const outlinedSelectedClassName: string | undefined = isOutlined && !!inputValue
    ? classes.fieldSelectOutlinedSelected : undefined;
  const rightOffsetClassName: string | undefined = withRightOffset ? classes.fieldSelectRightOffset : undefined;
  const shortClassName: string | undefined = isShort ? classes.fieldSelectShort : undefined;
  const inputPlainClassName: string | undefined = isPlain ? classes.fieldSelectInputPlain : undefined;
  const inputPlainOutlinedClassName: string | undefined = isPlain && isOutlined
    ? classes.fieldSelectInputPlainOutlined : undefined;
  const inputPlainNoBordersClassName: string | undefined = isPlain && withNoBorders
    ? classes.fieldSelectInputPlainNoBorders : undefined;
  const selectNoWrapClassName: string | undefined = withNoWrap ? classes.fieldSelectSelectNoWrap : undefined;

  const [currentValue, setCurrentValue]: [SelectItem | null, Function] = useState(null);

  const open: Function = useCallback((): void => {
    if (onOpen) {
      onOpen();
    }
  }, []);

  const close: Function = useCallback((): void => {
    if (onClose) {
      onClose(searchOptions);
    }
  }, [searchOptions]);

  const updateValue: Function = useCallback((newValue: SelectItem): void => {
    if (children && !!children.length && newValue && (!currentValue || currentValue.props.value !== newValue)) {
      setCurrentValue(children.find((listItem: SelectItem): boolean => (
        listItem.props && listItem.props.value === newValue
      )));
    }
  }, [children, currentValue]);

  useEffect((): void => {
    if (!isPlain && inputValue) {
      updateValue(inputValue);
    }
  }, [inputValue]);

  return (
    <FormControl
      ref={ref}
      error={!isPlain && withErrors}
      variant={isOutlined ? FORM_CONTROL_VARIANT_OUTLINED : undefined}
      className={FormatService.formatClassNames(
        classes.fieldSelect, noUnderlineClassName, iconClassName, lightClassName, borderedClassName, outlinedClassName,
        outlinedSelectedClassName, rightOffsetClassName, shortClassName, className,
      )}
      style={{
        width: width || undefined,
      }}
    >
      {
        !!label && (
          <InputLabel htmlFor={id || inputId} variant={isOutlined ? LABEL_VARIANT_OUTLINED : undefined}>
            {label}
            {
              isOutlined && isRequired && (
                <RequiredMark />
              )
            }
          </InputLabel>
        )
      }
      <Select
        displayEmpty
        open={isOpen}
        disabled={isDisabled}
        readOnly={isReadOnly}
        multiple={isMultiple}
        variant={isOutlined ? VARIANT_OUTLINED : undefined}
        ref={(
          inputRefs ? (ref: HTMLInputElement | null): void => {
            inputRefs.current[inputRefIndex] = ref;
          } : null
        )}
        input={(
          isOutlined && label ? (
            <OutlinedInput
              label={(
                <TextBlock>
                  <TextBlock>
                    {label}
                  </TextBlock>
                  {
                    isRequired && (
                      <RequiredMark />
                    )
                  }
                </TextBlock>
              )}
            />
          ) : undefined
        )}
        className={FormatService.formatClassNames(
          inputPlainClassName, inputPlainOutlinedClassName, inputPlainNoBordersClassName,
        )}
        classes={{
          select: FormatService.formatClassNames(classes.fieldSelectSelect, selectNoWrapClassName),
        }}
        SelectDisplayProps={{
          [DATA_REF_NAME]: inputRefIndex,
        }}
        value={isPlain ? (value || blankItemValueDefault) : undefined}
        renderValue={valueRendered}
        label={(
          isOutlined ? (
            <TextBlock>
              <TextBlock>
                {label}
              </TextBlock>
              {
                isRequired && (
                  <RequiredMark />
                )
              }
            </TextBlock>
          ) : label
        )}
        labelId={id || inputId}
        onOpen={open}
        onClose={close}
        onChange={(event: EventSelectChange, value: SelectItem): void => {
          if (onChange) {
            if (isPlain) {
              if (event.target) {
                onChange(event, (event.target as HTMLInputElement).value);
              }
            } else {
              onChange(event, value);
            }
          }
        }}
        inputProps={{
          id: id || inputId,
          autoComplete: AUTOCOMPLETE_OFF,
        }}
        {
          ...(!isPlain ? {
            ...input,
            value: inputValue,
          } : {})
        }
        {...custom}
      >
        {
          withSearch && onSearch && (
            <SearchDropdown
              searchOptions={searchOptions}
              optionsTotal={optionsTotal}
              onSearch={onSearch}
            />
          )
        }
        {
          withBlankElement && (
            <MenuItem
              value={blankItemValue}
              onPointerDown={onBlankElementPointerDown}
              onContextMenu={onBlankElementContextMenu}
            >
              {
                withBlankItemDefault ? (
                  <TextBlock>
                    {blankItemLabel}
                  </TextBlock>
                ) : (
                  <TextItalic>
                    {blankItemLabel}
                  </TextItalic>
                )
              }
            </MenuItem>
          )
        }
        {newItemComponent}
        {children}
        {
          !!currentValue && ((children && !!children.length && !children.find((listItem: SelectItem): boolean => (
            listItem.props && currentValue.props && listItem.props.value === currentValue.props.value
          ))) || !children || !children.length) && currentValue
        }
      </Select>
      {
        !isPlain && withFormHelper && withErrors && (
          <FormHelper isTouched={isTouched} formError={formError || formSubmitError} />
        )
      }
    </FormControl>
  );
}));

export default FieldSelect;
